import React, { useMemo, useState } from "react";
import { Icon } from "./Icon";

export type SortDir = "asc" | "desc" | null;
export type SortState<K extends string = string> = { key: K | null; dir: SortDir };

export function useSort<T, K extends string = string>(
  rows: T[],
  initialKey: K | null = null,
  initialDir: SortDir = null,
  accessors?: Partial<Record<K, (row: T) => string | number | null | undefined>>,
) {
  const [sort, setSort] = useState<SortState<K>>({ key: initialKey, dir: initialDir });

  const sortedRows = useMemo(() => {
    if (!sort.key || !sort.dir) return rows;
    const key = sort.key;
    const get = accessors?.[key] ?? ((r: T) => (r as unknown as Record<string, unknown>)[key] as string | number | null | undefined);
    const copy = [...rows].sort((a, b) => {
      const av = get(a);
      const bv = get(b);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") return av - bv;
      return String(av).localeCompare(String(bv), undefined, { numeric: true });
    });
    return sort.dir === "asc" ? copy : copy.reverse();
  }, [rows, sort, accessors]);

  const sortBy = (key: K) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: "asc" };
      if (prev.dir === "asc") return { key, dir: "desc" };
      return { key: null, dir: null };
    });
  };

  return { sortedRows, sort, sortBy };
}

type ThProps<K extends string> = {
  label: React.ReactNode;
  k?: K;
  sort?: SortState<K>;
  onClick?: () => void;
  align?: "left" | "right" | "center";
  width?: string | number;
};

export function Th<K extends string>({ label, k, sort, onClick, align = "left", width }: ThProps<K>) {
  const sortable = !!onClick && !!k;
  const active = sortable && sort?.key === k;
  return (
    <th
      className={sortable ? "sortable" : ""}
      onClick={onClick}
      style={{ textAlign: align, width }}
    >
      {label}
      {active && (
        <span className="sort">
          {sort?.dir === "asc" ? <Icon name="arrowUp" size={12} /> : <Icon name="arrowDown" size={12} />}
        </span>
      )}
    </th>
  );
}

type TableProps = { children: React.ReactNode };
export function Table({ children }: TableProps) {
  return (
    <div className="tbl-wrap">
      <table className="tbl">{children}</table>
    </div>
  );
}

type TableFooterProps = {
  total: number;
  totalAll: number;
  page: number;
  onPage: (next: number) => void;
  perPage: number;
  onPerPage: (next: number) => void;
  perPageOptions?: number[];
};

export function TableFooter({
  total,
  totalAll,
  page,
  onPage,
  perPage,
  onPerPage,
  perPageOptions = [10, 25, 50, 100],
}: TableFooterProps) {
  const pages = Math.max(1, Math.ceil(total / perPage));
  const from = total === 0 ? 0 : (page - 1) * perPage + 1;
  const to = Math.min(total, page * perPage);
  return (
    <div className="tbl-foot">
      <div>
        Rows per page:&nbsp;
        <select
          value={perPage}
          onChange={(e) => {
            onPerPage(Number(e.target.value));
            onPage(1);
          }}
        >
          {perPageOptions.map((n) => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
      </div>
      <div>
        <span className="mono tnum">{from}–{to}</span>
        {" of "}
        <span className="mono tnum">{totalAll}</span>
      </div>
      <div className="pg">
        <button className="pg-btn" onClick={() => onPage(Math.max(1, page - 1))} disabled={page <= 1} aria-label="Previous">
          <Icon name="chevLeft" size={14} />
        </button>
        <button className="pg-btn" onClick={() => onPage(Math.min(pages, page + 1))} disabled={page >= pages} aria-label="Next">
          <Icon name="chevRight" size={14} />
        </button>
      </div>
    </div>
  );
}
